import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FONTS, COLORS, BREAKPOINTS } from "../../utils/styleConstants";

const DARK_BLUE = "#20376e";
const ACCENT_ORANGE = "#ff7057";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 1.5rem;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  font-size: 1.25rem;
  color: ${DARK_BLUE};
`;

const ModalMessage = styled.p`
  color: #444;
  line-height: 1.5;
`;

const FieldContainer = styled.div`
  margin-top: 1.5rem;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${DARK_BLUE};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  font-family: ${FONTS.PRIMARY};
  border: 1px solid #ddd;
  border-radius: 1rem;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${ACCENT_ORANGE};
    box-shadow: 0 0 0 2px rgba(255, 112, 87, 0.25);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  font-family: ${FONTS.PRIMARY};
  border: 1px solid #ddd;
  border-radius: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${ACCENT_ORANGE};
    box-shadow: 0 0 0 2px rgba(255, 112, 87, 0.25);
  }
`;

const Caption = styled.p`
  color: #666;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  margin-bottom: 0;
  font-style: italic;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${FONTS.PRIMARY};
`;

const PrimaryButton = styled(Button)`
  background-color: ${DARK_BLUE};
  color: white;
  border: none;
  
  &:hover:not(:disabled) {
    background-color: #172b57;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(32, 55, 110, 0.5);
  }
  
  &:disabled {
    background-color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const OutlineButton = styled(Button)`
  background-color: transparent;
  color: ${ACCENT_ORANGE};
  border: 1px solid ${ACCENT_ORANGE};
  
  &:hover:not(:disabled) {
    background-color: #fff0e8;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 112, 87, 0.5);
  }
  
  &:disabled {
    color: #a0a0a0;
    border-color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const AcceptModal = ({ isOpen, onClose, onConfirm, isProcessing = false, offer = null }) => {
  const [address, setAddress] = useState("");
  const [counterpartyDni, setCounterpartyDni] = useState("");
  const [lawyerDni, setLawyerDni] = useState("");
  
  // Determine if lawyer DNI field should be visible
  const hasLawyer = offer && (
    offer.lawyer?.firstName || 
    offer.lawyer?.lastNames || 
    offer.lawyer?.idValue ||
    offer.lawyer_name || 
    offer.lawyer_last_name ||
    offer.lawyer_id
  );

  // Autopopulate fields when offer data changes
  useEffect(() => {
    if (offer && isOpen) {
      // Autopopulate counterparty DNI if available
      const counterpartyId = offer.counterparty?.idValue || "";
      setCounterpartyDni(counterpartyId);

      // Autopopulate lawyer DNI if available and lawyer exists
      if (hasLawyer) {
        const lawyerId = offer.lawyer?.idValue || 
                        offer.lawyer?.identityNumber || 
                        offer.lawyer?.identificationNumber ||
                        offer.lawyer_id ||
                        "";
        setLawyerDni(lawyerId);
      }
    }
  }, [offer, hasLawyer, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setAddress("");
      setCounterpartyDni("");
      setLawyerDni("");
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  // Validation logic - all required fields must be filled
  const isFormValid = () => {
    const addressValid = address.trim() !== "";
    const counterpartyDniValid = counterpartyDni.trim() !== "";
    const lawyerDniValid = !hasLawyer || lawyerDni.trim() !== "";
    
    return addressValid && counterpartyDniValid && lawyerDniValid;
  };
  
  const handleAccept = () => {
    if (!isFormValid()) return;
    
    onConfirm({
      address: address.trim(),
      counterpartyDni: counterpartyDni.trim(),
      lawyerDni: hasLawyer ? lawyerDni.trim() : null
    });
  };

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalTitle>Confirmar Aceptación</ModalTitle>
        <ModalMessage>
          Revisa detenidamente la oferta vinculante, ya que al aceptar la oferta, te comprometes legalmente a cumplir con los términos y condiciones establecidos en la misma. ¿Está seguro?
        </ModalMessage>
        
        <FieldContainer>
          <FieldLabel>Complete dirección postal *</FieldLabel>
          <TextArea
            placeholder="Para proceder con la firma complete la dirección completa incluyendo calle, número, ciudad, código postal y país"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={isProcessing}
          />
        </FieldContainer>

        <FieldContainer>
          <FieldLabel>Complete DNI/NIE *</FieldLabel>
          <Input
            type="text"
            placeholder="Para proceder con la firma complete su DNI/NIE"
            value={counterpartyDni}
            onChange={(e) => setCounterpartyDni(e.target.value)}
            disabled={isProcessing}
          />
        </FieldContainer>

        {hasLawyer && (
          <FieldContainer>
            <FieldLabel>Complete DNI/NIE del representante legal *</FieldLabel>
            <Input
              type="text"
              placeholder="Para proceder con la firma complete el DNI/NIE"
              value={lawyerDni}
              onChange={(e) => setLawyerDni(e.target.value)}
              disabled={isProcessing}
            />
          </FieldContainer>
        )}
        
        <ModalButtons>
          <OutlineButton onClick={onClose} disabled={isProcessing}>
            No
          </OutlineButton>
          <PrimaryButton 
            onClick={handleAccept} 
            disabled={isProcessing || !isFormValid()}
          >
            {isProcessing ? "Procesando..." : "Sí"}
          </PrimaryButton>
        </ModalButtons>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AcceptModal;